import axios from "axios";

// ────────────────────────────────────────────────────────────
// 🌍 ENVIRONMENT DETECTION
// ────────────────────────────────────────────────────────────
const ENV              = process.env.REACT_APP_ENV;
const CLOUD_BASE_URL   = process.env.REACT_APP_API_BASE_URL;
const DESKTOP_BASE_URL = process.env.REACT_APP_DESKTOP_APP_API_BASE_URL;

const BASE_URL =
  ENV === "desktop"
    ? DESKTOP_BASE_URL
    : CLOUD_BASE_URL;

// ────────────────────────────────────────────────────────────
// 🖥️ DEFAULT API  /  ☁️ CLOUD API
// ────────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: BASE_URL,
});

export const cloudApi = axios.create({
  baseURL: CLOUD_BASE_URL,
});

// ────────────────────────────────────────────────────────────
// 🚪 FORCE LOGOUT
// ────────────────────────────────────────────────────────────
const forceLogout = () => {
  localStorage.clear();
  window.location.href = "/Login";
};

// ────────────────────────────────────────────────────────────
// 🚫 USER SUSPENSION CHECK
//
// Backend sets:
// local_config.key = user_suspended
//
// Desktop frontend detects this immediately and forces logout.
// This prevents suspended staff from continuing offline usage.
// ────────────────────────────────────────────────────────────
const checkUserSuspended = async () => {
  if (ENV !== "desktop") return;

  const localToken = localStorage.getItem("access_token");

  if (!localToken) return;

  try {
    const res = await axios.get(
      `${DESKTOP_BASE_URL}/local-config/user_suspended`,
      {
        headers: {
          Authorization: `Bearer ${localToken}`,
        },
      }
    );

    const suspended =
      res?.data?.value === "true" ||
      res?.data?.user_suspended === true;

    if (suspended) {
      console.warn(
        "User account suspended — forcing logout."
      );

      forceLogout();
    }

  } catch (err) {
    // Silent fail
    // App should continue even if backend is temporarily unavailable
  }
};

// Only check suspension if user is already logged in
if (localStorage.getItem("access_token")) {
  checkUserSuspended();
}

// ────────────────────────────────────────────────────────────
// 🔐 TOKEN INJECTORS
// ────────────────────────────────────────────────────────────
const injectLocalToken = (config) => {
  const token = localStorage.getItem("access_token");

  if (token) {
    config.headers["Authorization"] =
      `Bearer ${token}`;
  }

  return config;
};

const injectCloudToken = (config) => {
  const token =
    ENV === "desktop"
      ? (
          localStorage.getItem("cloud_access_token") ||
          localStorage.getItem("access_token")
        )
      : localStorage.getItem("access_token");

  if (token) {
    config.headers["Authorization"] =
      `Bearer ${token}`;
  }

  return config;
};

// ────────────────────────────────────────────────────────────
// 🧠 REFRESH BASE URL
// ────────────────────────────────────────────────────────────
const getAuthBaseURL = () => {
  if (ENV !== "desktop") {
    return CLOUD_BASE_URL;
  }

  const authMode =
    localStorage.getItem("auth_mode");

  return authMode === "desktop"
    ? DESKTOP_BASE_URL
    : CLOUD_BASE_URL;
};

// ────────────────────────────────────────────────────────────
// 🔁 SHARED LOCAL REFRESH STATE
// ────────────────────────────────────────────────────────────
let isRefreshing   = false;
let failedQueue    = [];
let refreshPromise = null;

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else       prom.resolve(token);
  });

  failedQueue = [];
};

const doTokenRefresh = () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;

  refreshPromise = (async () => {
    try {
      const refreshToken =
        localStorage.getItem("refresh_token");

      if (!refreshToken) {
        throw new Error("No refresh token");
      }

      const res = await axios.post(
        `${getAuthBaseURL()}/auth/refresh`,
        {
          refresh_token: refreshToken,
        }
      );

      const newAccessToken =
        res.data.access_token;

      const newRefreshToken =
        res.data.refresh_token;

      localStorage.setItem(
        "access_token",
        newAccessToken
      );

      localStorage.setItem(
        "refresh_token",
        newRefreshToken
      );

      api.defaults.headers.common["Authorization"] =
        `Bearer ${newAccessToken}`;

      if (ENV !== "desktop") {
        cloudApi.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
      }

      processQueue(null, newAccessToken);

      return newAccessToken;

    } catch (err) {

      processQueue(err, null);

      forceLogout();

      throw err;

    } finally {

      isRefreshing   = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

// ────────────────────────────────────────────────────────────
// 🔁 LOCAL API 401 HANDLER
// ────────────────────────────────────────────────────────────
const handleLocalApi401 = async (error) => {
  const originalRequest = error.config;

  if (
    error.response?.status !== 401 ||
    originalRequest._retry
  ) {
    return Promise.reject(error);
  }

  originalRequest._retry = true;

  if (isRefreshing) {
    return new Promise((resolve, reject) => {

      failedQueue.push({
        resolve,
        reject,
      });

    })
      .then((token) => {

        originalRequest.headers["Authorization"] =
          `Bearer ${token}`;

        return api(originalRequest);

      })
      .catch((err) => Promise.reject(err));
  }

  try {

    const token = await doTokenRefresh();

    originalRequest.headers["Authorization"] =
      `Bearer ${token}`;

    return api(originalRequest);

  } catch (err) {

    return Promise.reject(err);
  }
};

// ────────────────────────────────────────────────────────────
// ☁️ CLOUD TOKEN REFRESH
// ────────────────────────────────────────────────────────────
let isRefreshingCloud   = false;
let failedCloudQueue    = [];
let cloudRefreshPromise = null;

const processCloudQueue = (
  error,
  token = null
) => {

  failedCloudQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else       prom.resolve(token);
  });

  failedCloudQueue = [];
};

const doCloudTokenRefresh = () => {

  if (cloudRefreshPromise) {
    return cloudRefreshPromise;
  }

  isRefreshingCloud = true;

  cloudRefreshPromise = (async () => {

    try {

      // ─────────────────────────────────────
      // DESKTOP MODE
      // ─────────────────────────────────────
      if (ENV === "desktop") {

        const localAccessToken =
          localStorage.getItem("access_token");

        if (!localAccessToken) {

          forceLogout();

          throw new Error("No local session");
        }

        const res = await axios.get(
          `${DESKTOP_BASE_URL}/auth/cloud-token`,
          {
            headers: {
              Authorization:
                `Bearer ${localAccessToken}`,
            },
          }
        );

        const newCloudAccessToken =
          res.data.cloud_access_token;

        const newCloudRefreshToken =
          res.data.cloud_refresh_token;

        if (!newCloudAccessToken) {
          throw new Error(
            "Backend returned no cloud access token"
          );
        }

        localStorage.setItem(
          "cloud_access_token",
          newCloudAccessToken
        );

        if (newCloudRefreshToken) {

          localStorage.setItem(
            "cloud_refresh_token",
            newCloudRefreshToken
          );
        }

        cloudApi.defaults.headers.common["Authorization"] =
          `Bearer ${newCloudAccessToken}`;

        processCloudQueue(
          null,
          newCloudAccessToken
        );

        return newCloudAccessToken;
      }

      // ─────────────────────────────────────
      // PRODUCTION MODE
      // ─────────────────────────────────────
      else {

        const refreshToken =
          localStorage.getItem("refresh_token");

        if (!refreshToken) {

          forceLogout();

          throw new Error("No refresh token");
        }

        const res = await axios.post(
          `${CLOUD_BASE_URL}/auth/refresh`,
          {
            refresh_token: refreshToken,
          }
        );

        const newAccessToken =
          res.data.access_token;

        const newRefreshToken =
          res.data.refresh_token;

        localStorage.setItem(
          "access_token",
          newAccessToken
        );

        localStorage.setItem(
          "refresh_token",
          newRefreshToken
        );

        api.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;

        cloudApi.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;

        processCloudQueue(
          null,
          newAccessToken
        );

        return newAccessToken;
      }

    } catch (err) {

      processCloudQueue(err, null);

      const status =
        err.response?.status;

      const code =
        err.response?.data?.code;

      // ─────────────────────────────────────────────
      // USER SUSPENDED
      //
      // User was validly logged in already.
      // Sync attempted.
      // Backend explicitly says account is suspended.
      // Force logout immediately.
      // ─────────────────────────────────────────────
      if (code === "USER_SUSPENDED") {

        console.warn("User suspended.");

        forceLogout();
      }

      // ─────────────────────────────────────────────
      // TOKEN EXPIRED / INVALID
      // ─────────────────────────────────────────────
      else if (
        status === 401 ||
        status === 404
      ) {

        forceLogout();
      }

      // ─────────────────────────────────────────────
      // OTHER 403 ERRORS
      //
      // DO NOT LOG USER OUT
      // ─────────────────────────────────────────────
      else if (status === 403) {

        console.warn(
          "403 Forbidden:",
          err.response?.data
        );
      }

      // 503/offline → ignore

      throw err;

    } finally {

      isRefreshingCloud   = false;
      cloudRefreshPromise = null;
    }
  })();

  return cloudRefreshPromise;
};

const handleCloudApi401 = async (error) => {

  const originalRequest =
    error.config;

  if (
    error.response?.status !== 401 ||
    originalRequest._retry
  ) {
    return Promise.reject(error);
  }

  originalRequest._retry = true;

  if (isRefreshingCloud) {

    return new Promise((resolve, reject) => {

      failedCloudQueue.push({
        resolve,
        reject,
      });

    })
      .then((token) => {

        originalRequest.headers["Authorization"] =
          `Bearer ${token}`;

        return cloudApi(originalRequest);

      })
      .catch((err) => Promise.reject(err));
  }

  try {

    const token =
      await doCloudTokenRefresh();

    originalRequest.headers["Authorization"] =
      `Bearer ${token}`;

    return cloudApi(originalRequest);

  } catch (err) {

    return Promise.reject(err);
  }
};

// ────────────────────────────────────────────────────────────
// ⚙️ INTERCEPTORS
// ────────────────────────────────────────────────────────────
api.interceptors.request.use(
  injectLocalToken
);

api.interceptors.response.use(
  (res) => res,
  handleLocalApi401
);

cloudApi.interceptors.request.use(
  injectCloudToken
);

cloudApi.interceptors.response.use(
  (res) => res,
  handleCloudApi401
);

// ────────────────────────────────────────────────────────────
// 🔄 SILENT CLOUD TOKEN MIGRATION
// ────────────────────────────────────────────────────────────
if (ENV === "desktop") {

  const cloudAccessMissing =
    !localStorage.getItem(
      "cloud_access_token"
    );

  const hasLocalSession =
    !!localStorage.getItem(
      "access_token"
    );

  if (
    cloudAccessMissing &&
    hasLocalSession
  ) {

    axios
      .get(
        `${DESKTOP_BASE_URL}/auth/cloud-token`,
        {
          headers: {
            Authorization:
              `Bearer ${localStorage.getItem("access_token")}`,
          },
        }
      )

      .then((res) => {

        if (res.data.cloud_access_token) {

          localStorage.setItem(
            "cloud_access_token",
            res.data.cloud_access_token
          );

          if (res.data.cloud_refresh_token) {

            localStorage.setItem(
              "cloud_refresh_token",
              res.data.cloud_refresh_token
            );
          }

          cloudApi.defaults.headers.common["Authorization"] =
            `Bearer ${res.data.cloud_access_token}`;
        }
      })

      .catch(() => {
        // Non-fatal
      });
  }
}

// ────────────────────────────────────────────────────────────
export default api;